import styled from "@emotion/styled"
import useSSR from "hooks/useSSR"

import { Icon } from "components/icon/Icon"
import { useDictionary } from "contexts/Dictionary"
import { useEnv } from "contexts/Env"
import { cssVarExpansion, cssVarHeight, cssVarTransitionDelay, cssVarTransitionDuration } from "contexts/Header"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import getMediaQuery from "css/breakpoints"
import { buttonIcon, opaqueGreen } from "css/buttons"
import { fullGrid } from "css/grid"
import { bold } from "css/text"
import { useWatchgrid } from "./context"
import { getOriginLevel2, sendWatchGridEvent } from "./events"
import { allParams, getAllParams, getCurrentUrl, removeParam } from "./utils"

const Root = styled.div`
  --subnav-ofs: calc(var(--subnav-height) * var(--is-sub));

  ${fullGrid}
  position: sticky;
  top: calc((var(${cssVarHeight}) * var(${cssVarExpansion}) + var(--subnav-ofs) - 1px));
  transition: top var(${cssVarTransitionDuration}) var(${cssVarTransitionDelay});
  z-index: 10;
  background: rgb(var(--pure-white));
  overflow-x: auto;
  margin-block-end: 20px;
`
const List = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: max-content;
`
const Item = styled.li`
  margin-inline-end: 20px;

  &:first-of-type {
    margin-inline-start: var(--outer-margin);
  }

  &:last-of-type {
    margin-inline-end: var(--outer-margin);

    ${getMediaQuery("xl")} {
      margin-inline-end: 20px;
    }
  }
`
const Button = styled.button`
  font-size: 16px;
  line-height: 80px;
  ${bold}
  display: inline-flex;
  align-items: center;
  color: rgb(var(--green));
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  span {
    ${buttonIcon}
    ${opaqueGreen}
    height: 20px;
    width: 20px;
    margin-inline-start: 10px;
    pointer-events: none;

    svg {
      width: 6px;
      height: 6px;
    }
  }
`

export default function SelectedFilters({ advancedFilters, quickFilters, gridData, searchUids, searchHubs, pipelines }) {
  const ssr = useSSR()
  const dictionary = useDictionary()
  const navigation = useNavigation()
  const env = useEnv()
  const locale = useLocale()

  const { setFilterStrip } = useWatchgrid()

  const currentParams = getAllParams()

  const onFilterRemoved = e => {
    const { name, value } = e.target

    const currentUrl = getCurrentUrl()

    const params = currentUrl.searchParams

    removeParam(name, value, params)

    // coveo event
    searchUids.forEach((searchUid, index) => {
      const token = pipelines[index]

      sendWatchGridEvent(env.organizationId, token, "search", {
        actionCause: "facetDeselect",
        language: locale?.current?.codes?.www,
        numberOfResults: gridData.length,
        queryText: "",
        responseTime: 0,
        searchQueryUid: searchUid,
        searchHub: searchHubs[index],
        anonymous: true,
        originLevel1: searchHubs[index],
        originLevel2: getOriginLevel2(currentParams?.global_category?.[0]),
        userAgent: navigator?.userAgent,
        customData: {
          facetField: name,
          facetValue: value,
          facetId: "family",
          facetTitle: "collection",
        },
      })
    })

    navigation.push(currentUrl.href, { nopageview: true }, { scroll: false })

    const validArray = Array.from(params).filter(v => !["group", "sort"].includes(v[0]))
    setFilterStrip(validArray.length > 0)
  }

  return (
    <Root style={{ "--is-sub": !ssr && document.querySelector(".header-subnav") ? 1 : 0 }}>
      <List>
        {Object.entries(currentParams).map(([facet, value], i) => {
          if (allParams.includes(facet) && facet !== "sort") {
            const content = []

            const facetObj = advancedFilters.find(item => item.facet === facet)
            const facetValues = facetObj ? facetObj.values : quickFilters

            value.forEach(element => {
              const filterObj = facetValues.find(item => item.value === element)

              content.push(
                <Item key={filterObj.key}>
                  <Button
                    name={facet}
                    value={filterObj.value}
                    onClick={onFilterRemoved}
                    aria-label={`${filterObj.localizedValue} - ${dictionary.ariaLabelRemoveFilter()}`}
                  >
                    {filterObj.localizedValue}
                    <span>
                      <Icon type='close' />
                    </span>
                  </Button>
                </Item>
              )
            })

            return content
          }
        })}
      </List>
    </Root>
  )
}
